// dashboard.js
import React, { useEffect, useState, useContext } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Paper,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Container,
  Avatar,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Zoom,
  Fade,
  alpha,
  LinearProgress,
  Button,
  Badge,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  TrendingUp,
  Package,
  ShoppingBag,
  ChevronDown,
  PoundSterling,
  ClipboardX,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Users,
  Store,
  Clock,
  AlertTriangle,
  Download,
  RefreshCw,
  Eye,
  MoreVertical,
} from "lucide-react";
import { collection, query, onSnapshot, getDocs } from "firebase/firestore";
import { db } from "../firebase-config";
import fetchRestaurantPerformance from "./fetchRestaurantPerformance";
import TodayInvoicesGrid from "../components/TodayInvoicesGrid";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { DateTime } from "luxon";
import SalesCards from "../components/SalesCards";
import OrderTimeSettings from "./OrderTimeSettings";
import { AuthContext } from '../auth-context';

const Dashboard = () => {
  const theme = useTheme();
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [restaurantPerformance, setRestaurantPerformance] = useState([]);
  const [salesTrend, setSalesTrend] = useState([]);
  const [todayOrders, setTodayOrders] = useState({});
  const [totalAvailable, setTotalAvailable] = useState({});
  const [totalSold, setTotalSold] = useState({});
  const [totalCombined, setTotalCombined] = useState({});
  const [todayRestaurantOrders, setTodayRestaurantOrders] = useState([]);
  const [restaurantUnpaidRevenue, setRestaurantUnpaidRevenue] = useState([]);
  const [isCutoffEnabled, setIsCutoffEnabled] = useState(false);
  const [timeRange, setTimeRange] = useState('week');
  const [revenueTimeRange, setRevenueTimeRange] = useState('week');
  const [categoryTimeRange, setCategoryTimeRange] = useState('week');
  const [performanceTimeRange, setPerformanceTimeRange] = useState('week');
  const [refreshing, setRefreshing] = useState(false);
  const [allInvoices, setAllInvoices] = useState([]);
  const { user } = useContext(AuthContext);

  const isSuperAdmin = user?.role === 'superadmin';

  const COLORS = [theme.palette.primary.main, theme.palette.secondary.main, "#FF8042", "#00C49F"];

  // Helper: get cutoff date for a given time range
  const getDateCutoff = (range) => {
    const now = new Date();
    switch (range) {
      case 'day':
        return new Date(now.getFullYear(), now.getMonth(), now.getDate());
      case 'week': {
        const d = new Date(now);
        d.setDate(d.getDate() - 7);
        return d;
      }
      case 'month': {
        const d = new Date(now);
        d.setMonth(d.getMonth() - 1);
        return d;
      }
      case 'quarter': {
        const d = new Date(now);
        d.setMonth(d.getMonth() - 3);
        return d;
      }
      case 'year': {
        const d = new Date(now);
        d.setFullYear(d.getFullYear() - 1);
        return d;
      }
      default:
        return new Date(0);
    }
  };

  // Helper: parse invoice date
  const parseInvoiceDate = (createdAt) => {
    if (createdAt && createdAt.seconds) {
      return new Date(createdAt.seconds * 1000);
    } else if (typeof createdAt === 'string') {
      return new Date(createdAt);
    }
    return null;
  };

  // Reusable time range buttons component
  const TimeRangeButtons = ({ value, onChange, options }) => {
    const ranges = options || ['day', 'week', 'month', 'quarter', 'year'];
    return (
      <Box display="flex" gap={0.5} flexWrap="wrap">
        {ranges.map((range) => (
          <Button
            key={range}
            size="small"
            variant={value === range ? 'contained' : 'outlined'}
            onClick={() => onChange(range)}
            sx={{
              textTransform: 'capitalize',
              borderRadius: 2,
              minWidth: 'auto',
              px: 1.5,
              fontSize: '0.75rem',
            }}
          >
            {range}
          </Button>
        ))}
      </Box>
    );
  };

  const checkAndUpdateCutoffStatus = async () => {
    const londonTime = DateTime.now().setZone("Europe/London");
    try {
      const docRef = doc(db, "cutoffTime", "O5uOFJFkCskUD6roE237");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setIsCutoffEnabled(docSnap.data().isCutoffEnabled);
      }
    } catch (error) {
      console.error("Error fetching cutoff status:", error);
    }
  };

  useEffect(() => {
    checkAndUpdateCutoffStatus();
  }, []);

  const handleToggleChange = async () => {
    const newValue = !isCutoffEnabled;
    setIsCutoffEnabled(newValue);
    try {
      const docRef = doc(db, "cutoffTime", "O5uOFJFkCskUD6roE237");
      await updateDoc(docRef, { isCutoffEnabled: newValue });
    } catch (error) {
      console.error("Error updating cutoff status:", error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 1000);
  };

  const getResturantPerformanceData = async (range) => {
    const cutoff = getDateCutoff(range || performanceTimeRange);
    const restaurantPerformanceData = await fetchRestaurantPerformance(db, cutoff);
    setRestaurantPerformance(restaurantPerformanceData);
  };

  useEffect(() => {
    const todayStart = new Date();
    todayStart.setUTCHours(0, 0, 0, 0);

    const invoicesRef = collection(db, "invoices");

    const unsubscribeOrders = onSnapshot(invoicesRef, (snapshot) => {
      const ordersCount = {};
      const statusCount = new Map();
      const restaurantOrders = new Map();

      // Store parsed invoices for later filtering
      const parsedInvoices = [];

      snapshot.forEach((docSnap) => {
        const invoice = docSnap.data();
        const invoiceDate = parseInvoiceDate(invoice.createdAt);

        parsedInvoices.push({ ...invoice, _parsedDate: invoiceDate });

        if (invoiceDate && invoiceDate >= todayStart) {
          if (invoice.restaurantName) {
            restaurantOrders.set(
              invoice.restaurantName,
              (restaurantOrders.get(invoice.restaurantName) || 0) + 1
            );
            ordersCount[invoice.restaurantName] =
              (ordersCount[invoice.restaurantName] || 0) + 1;
          }
        }

        const status =
          (invoice.orderStatus || "Pending").charAt(0).toUpperCase() +
          (invoice.orderStatus || "Pending").slice(1);
        statusCount.set(status, (statusCount.get(status) || 0) + 1);
      });

      setAllInvoices(parsedInvoices);
      setOrderStatusData(Array.from(statusCount, ([name, value]) => ({ name, value })));
      setTodayOrders(ordersCount);
      setTodayRestaurantOrders(Array.from(restaurantOrders, ([name, orders]) => ({ name, orders })));
    });

    const inventoryRef = collection(db, "inventoryItems");

    const unsubscribeTotalAvailable = onSnapshot(inventoryRef, async (snapshot) => {
      let totalAvailableItems = 0;
      let totalAvailableCost = 0;
      let totalAvailableQuantity = 0;
      const lowStock = [];

      snapshot.forEach((doc) => {
        const item = doc.data();
        totalAvailableItems += 1;
        totalAvailableCost += parseFloat(item.price) * Number(item.availableQuantity) || 0;
        totalAvailableQuantity += Number(item.availableQuantity) || 0;

        if (Number(item.availableQuantity) <= 10) {
          lowStock.push({
            title: item.title,
            quantity: Number(item.availableQuantity),
            threshold: 10,
          });
        }
      });

      setTotalAvailable({
        items: totalAvailableItems,
        cost: totalAvailableCost.toFixed(2),
        quantity: totalAvailableQuantity,
      });
      setLowStockItems(lowStock);

      // Categories are now computed in a separate effect based on categoryTimeRange
    });

    const unsubscribeTotalSold = onSnapshot(invoicesRef, (snapshot) => {
      let totalSoldItems = 0;
      let totalSoldCost = 0;
      let totalSoldQuantity = 0;
      let totalPaid = 0;
      let totalUnpaid = 0;

      snapshot.forEach((doc) => {
        const invoice = doc.data();
        let invoiceTotalCost = 0;

        invoice.items.forEach((item) => {
          totalSoldItems += 1;
          const itemCost = parseFloat(item.price) * Number(item.quantity) || 0;
          invoiceTotalCost += itemCost;
          totalSoldCost += itemCost;
          totalSoldQuantity += Number(item.quantity) || 0;
        });

        if (invoice.isBillPaid) {
          totalPaid += invoiceTotalCost;
        } else {
          totalUnpaid += invoiceTotalCost;
        }
      });

      setTotalSold({
        items: totalSoldItems,
        cost: totalSoldCost.toFixed(2),
        quantity: totalSoldQuantity,
        totalPaid: totalPaid.toFixed(2),
        totalUnpaid: totalUnpaid.toFixed(2),
      });
    });

    getResturantPerformanceData('week');

    return () => {
      unsubscribeOrders();
      unsubscribeTotalAvailable();
      unsubscribeTotalSold();
    };
  }, []);

  useEffect(() => {
    const combinedItems = (totalAvailable.items || 0) + (totalSold.items || 0);
    const combinedCost = (parseFloat(totalAvailable.cost) || 0) + (parseFloat(totalSold.cost) || 0);
    const combinedQuantity = (totalAvailable.quantity || 0) + (totalSold.quantity || 0);

    setTotalCombined({
      items: combinedItems,
      cost: combinedCost.toFixed(2),
      quantity: combinedQuantity,
    });
  }, [totalAvailable, totalSold]);

  // Effect: compute sales trend data based on timeRange
  useEffect(() => {
    if (allInvoices.length === 0) return;
    const cutoff = getDateCutoff(timeRange);
    const salesData = [];

    // Determine date format based on range
    const getDateLabel = (date) => {
      if (timeRange === 'year') {
        return date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
      }
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    };

    allInvoices.forEach((invoice) => {
      const invoiceDate = invoice._parsedDate;
      if (invoiceDate && invoiceDate >= cutoff) {
        const formattedDate = getDateLabel(invoiceDate);
        const invoiceTotal = (invoice.items || []).reduce(
          (sum, item) => sum + (item.price * item.quantity || 0), 0
        );

        const existingDay = salesData.find((item) => item.date === formattedDate);
        if (existingDay) {
          existingDay.Sales += invoiceTotal;
        } else {
          salesData.push({ date: formattedDate, Sales: invoiceTotal });
        }
      }
    });

    salesData.sort((a, b) => {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const partsA = a.date.split(' ');
      const partsB = b.date.split(' ');
      if (partsA.length === 2 && partsB.length === 2) {
        const monthIdxA = months.indexOf(partsA[1]) !== -1 ? months.indexOf(partsA[1]) : months.indexOf(partsA[0]);
        const monthIdxB = months.indexOf(partsB[1]) !== -1 ? months.indexOf(partsB[1]) : months.indexOf(partsB[0]);
        const dayA = parseInt(partsA[0]) || 0;
        const dayB = parseInt(partsB[0]) || 0;
        return monthIdxA * 31 + dayA - (monthIdxB * 31 + dayB);
      }
      return 0;
    });

    setSalesTrend(salesData);
  }, [allInvoices, timeRange]);

  // Effect: compute pending revenue by restaurant based on revenueTimeRange
  useEffect(() => {
    if (allInvoices.length === 0) return;
    const cutoff = getDateCutoff(revenueTimeRange);
    const restaurantUnpaid = new Map();

    allInvoices.forEach((invoice) => {
      const invoiceDate = invoice._parsedDate;
      if (!invoice.isBillPaid && invoice.restaurantName && invoiceDate && invoiceDate >= cutoff) {
        const invoiceTotal = (invoice.items || []).reduce(
          (sum, item) => sum + (item.price * item.quantity || 0), 0
        );
        restaurantUnpaid.set(
          invoice.restaurantName,
          (restaurantUnpaid.get(invoice.restaurantName) || 0) + invoiceTotal
        );
      }
    });

    setRestaurantUnpaidRevenue(
      Array.from(restaurantUnpaid, ([name, unpaid]) => ({
        name,
        unpaid: parseFloat(unpaid.toFixed(2)),
      }))
    );
  }, [allInvoices, revenueTimeRange]);

  // Effect: compute sales by category based on categoryTimeRange
  useEffect(() => {
    if (allInvoices.length === 0) return;

    const computeCategoryData = async () => {
      const cutoff = getDateCutoff(categoryTimeRange);
      const categoryRef = collection(db, 'inventoryCategory');
      const categorySnapshot = await getDocs(categoryRef);

      const categories = categorySnapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().category,
      }));

      // Aggregate sales from invoices by matching item categoryId
      const categorySales = {};
      categories.forEach((cat) => { categorySales[cat.id] = 0; });

      // Get inventory items to map item titles to categories
      const inventoryRef = collection(db, 'inventoryItems');
      const inventorySnapshot = await getDocs(inventoryRef);
      const itemCategoryMap = {};
      inventorySnapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (data.title && data.categoryId) {
          itemCategoryMap[data.title.toLowerCase()] = data.categoryId;
        }
      });

      allInvoices.forEach((invoice) => {
        const invoiceDate = invoice._parsedDate;
        if (invoiceDate && invoiceDate >= cutoff) {
          (invoice.items || []).forEach((item) => {
            const catId = itemCategoryMap[(item.title || item.name || '').toLowerCase()];
            if (catId && categorySales[catId] !== undefined) {
              categorySales[catId] += (item.price * item.quantity) || 0;
            }
          });
        }
      });

      const result = categories.map((cat) => ({
        name: cat.name,
        Sales: parseFloat((categorySales[cat.id] || 0).toFixed(2)),
      }));
      setCategoryData(result);
    };

    computeCategoryData();
  }, [allInvoices, categoryTimeRange]);

  // Effect: re-fetch restaurant performance when performanceTimeRange changes
  useEffect(() => {
    getResturantPerformanceData(performanceTimeRange);
  }, [performanceTimeRange]);

  const TodayOrdersTable = ({ data }) => {
    return (
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          boxShadow: 'none',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
              <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                Restaurant Name
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }} align="right">
                Orders
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow
                key={row.name}
                sx={{
                  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.02) },
                  transition: 'background-color 0.2s',
                }}
              >
                <TableCell>{row.name}</TableCell>
                <TableCell align="right">
                  <Chip
                    label={row.orders}
                    size="small"
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const TodayOrdersChart = ({ data }) => {
    if (!data || data.length === 0) {
      return (
        <Box
          sx={{
            height: 300,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            backgroundColor: alpha(theme.palette.background.default, 0.5),
            borderRadius: 2,
          }}
        >
          <ClipboardX size={48} color={theme.palette.text.secondary} />
          <Typography variant="h6" color="text.secondary" align="center">
            No orders received today
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            New orders will appear here as they come in
          </Typography>
        </Box>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.1)} />
          <XAxis dataKey="name" stroke={theme.palette.text.secondary} />
          <YAxis stroke={theme.palette.text.secondary} />
          <RechartsTooltip
            contentStyle={{
              borderRadius: 8,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: theme.shadows[4],
            }}
          />
          <Bar
            dataKey="orders"
            fill={theme.palette.primary.main}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const StatCard = ({ title, value, icon, color, trend, trendValue }) => (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: 2,
        transition: 'transform 0.3s, box-shadow 0.3s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 8,
        },
        position: 'relative',
        overflow: 'visible',
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ color: theme.palette.text.primary }}>
              {value}
            </Typography>
            {trend && (
              <Box display="flex" alignItems="center" mt={1}>
                {trend === 'up' ? (
                  <ArrowUpRight size={16} color={theme.palette.success.main} />
                ) : (
                  <ArrowDownRight size={16} color={theme.palette.error.main} />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: trend === 'up' ? theme.palette.success.main : theme.palette.error.main,
                    ml: 0.5,
                  }}
                >
                  {trendValue}
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: alpha(color, 0.1),
              color: color,
              width: 56,
              height: 56,
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{
      minHeight: "100vh",
      backgroundColor: theme.palette.background.default,
    }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 0,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          backgroundColor: theme.palette.background.paper,
          position: 'sticky',
          top: 0,
          zIndex: 1000,
        }}
      >
        <Container maxWidth="xl" sx={{ py: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: theme.palette.text.primary }}>
                Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Welcome back! Here's what's happening with your business.
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Chip
                icon={<Clock size={16} />}
                label={DateTime.now().setZone("Europe/London").toFormat("dd MMM yyyy, hh:mm a")}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              />
              <Tooltip title="Refresh Data" arrow TransitionComponent={Zoom}>
                <IconButton
                  onClick={handleRefresh}
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) },
                  }}
                >
                  <RefreshCw size={20} className={refreshing ? 'spin' : ''} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {isSuperAdmin && <OrderTimeSettings />}

        {/* KPI Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Revenue"
              value={`£${totalSold.cost || 0}`}
              icon={<PoundSterling />}
              color={theme.palette.primary.main}
              trend="up"
              trendValue="+12.5%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Paid Revenue"
              value={`£${totalSold.totalPaid || 0}`}
              icon={<TrendingUp />}
              color={theme.palette.success.main}
              trend="up"
              trendValue="+8.2%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Pending Revenue"
              value={`£${totalSold.totalUnpaid || 0}`}
              icon={<Package />}
              color={theme.palette.warning.main}
              trend="down"
              trendValue="-3.1%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Orders"
              value={totalSold.items || 0}
              icon={<ShoppingBag />}
              color={theme.palette.secondary.main}
              trend="up"
              trendValue="+15.3%"
            />
          </Grid>
        </Grid>

        <SalesCards />

        {/* Charts Grid */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* Sales Trend */}
          <Grid item xs={12} lg={8}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: 2,
                transition: 'box-shadow 0.3s',
                '&:hover': { boxShadow: 4 },
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
                <Typography variant="h6" fontWeight="bold" sx={{ color: theme.palette.text.primary }}>
                  Sales Trend
                </Typography>
                <TimeRangeButtons value={timeRange} onChange={setTimeRange} options={['week', 'month', 'quarter', 'year']} />
              </Box>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={salesTrend} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.1)} />
                  <XAxis dataKey="date" stroke={theme.palette.text.secondary} />
                  <YAxis stroke={theme.palette.text.secondary} />
                  <RechartsTooltip
                    formatter={(value) => [`£${parseFloat(value).toFixed(2)}`, "Sales"]}
                    contentStyle={{
                      borderRadius: 8,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      boxShadow: theme.shadows[4],
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="Sales"
                    stroke={theme.palette.primary.main}
                    strokeWidth={3}
                    dot={{ fill: theme.palette.primary.main, strokeWidth: 2 }}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Order Status Distribution */}
          <Grid item xs={12} lg={4}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: 2,
                transition: 'box-shadow 0.3s',
                '&:hover': { boxShadow: 4 },
                height: '100%',
              }}
            >
              <Typography variant="h6" fontWeight="bold" sx={{ color: theme.palette.text.primary }} mb={3}>
                Order Status
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={orderStatusData}
                    cx="50%"
                    cy="45%"
                    innerRadius={55}
                    outerRadius={75}
                    fill="#8884d8"
                    dataKey="value"
                    label={false}
                  >
                    {orderStatusData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(value, name) => [value, name]}
                    contentStyle={{
                      borderRadius: 8,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      boxShadow: theme.shadows[4],
                    }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value) => <span style={{ color: theme.palette.text.primary, fontSize: 13 }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Today's Orders by Restaurant */}
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: 2,
                transition: 'box-shadow 0.3s',
                '&:hover': { boxShadow: 4 },
              }}
            >
              <Typography variant="h6" fontWeight="bold" sx={{ color: theme.palette.text.primary }} mb={3}>
                Today's Orders by Restaurant
              </Typography>
              <TodayOrdersTable data={todayRestaurantOrders} />
            </Paper>
          </Grid>

          {/* Restaurant Unpaid Revenue */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: 2,
                transition: 'box-shadow 0.3s',
                '&:hover': { boxShadow: 4 },
              }}
            >
              <Box mb={2}>
                <Typography variant="h6" fontWeight="bold" sx={{ color: theme.palette.text.primary }} mb={1}>
                  Pending Revenue by Restaurant
                </Typography>
                <TimeRangeButtons value={revenueTimeRange} onChange={setRevenueTimeRange} />
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={restaurantUnpaidRevenue} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.1)} />
                  <XAxis dataKey="name" stroke={theme.palette.text.secondary} />
                  <YAxis stroke={theme.palette.text.secondary} />
                  <RechartsTooltip
                    formatter={(value) => [`£${value}`, "Pending Amount"]}
                    contentStyle={{
                      borderRadius: 8,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      boxShadow: theme.shadows[4],
                    }}
                  />
                  <Bar
                    dataKey="unpaid"
                    fill={theme.palette.primary.main}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Category Performance */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: 2,
                transition: 'box-shadow 0.3s',
                '&:hover': { boxShadow: 4 },
              }}
            >
              <Box mb={2}>
                <Typography variant="h6" fontWeight="bold" sx={{ color: theme.palette.text.primary }} mb={1}>
                  Sales By Category
                </Typography>
                <TimeRangeButtons value={categoryTimeRange} onChange={setCategoryTimeRange} />
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={categoryData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.1)} />
                  <XAxis dataKey="name" stroke={theme.palette.text.secondary} />
                  <YAxis stroke={theme.palette.text.secondary} />
                  <RechartsTooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      boxShadow: theme.shadows[4],
                    }}
                  />
                  <Bar
                    dataKey="Sales"
                    fill={theme.palette.secondary.main}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Restaurant Performance */}
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: 2,
                transition: 'box-shadow 0.3s',
                '&:hover': { boxShadow: 4 },
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
                <Typography variant="h6" fontWeight="bold" sx={{ color: theme.palette.text.primary }}>
                  Performance by Restaurant
                </Typography>
                <TimeRangeButtons value={performanceTimeRange} onChange={setPerformanceTimeRange} />
              </Box>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={restaurantPerformance} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.1)} />
                  <XAxis dataKey="name" stroke={theme.palette.text.secondary} />
                  <YAxis stroke={theme.palette.text.secondary} />
                  <RechartsTooltip
                    formatter={(value) => [`£${parseFloat(value).toFixed(2)}`, "Revenue"]}
                    contentStyle={{
                      borderRadius: 8,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      boxShadow: theme.shadows[4],
                    }}
                  />
                  <Bar
                    dataKey="revenue"
                    fill={theme.palette.primary.main}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* <TodayInvoicesGrid /> */}

          {/* Low Stock Alerts */}
          <Grid item xs={12}>
            <Accordion
              sx={{
                borderRadius: 3,
                boxShadow: 2,
                '&:before': { display: 'none' },
                overflow: 'hidden',
              }}
            >
              <AccordionSummary
                expandIcon={<ChevronDown />}
                sx={{
                  backgroundColor: alpha(theme.palette.warning.main, 0.05),
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <AlertTriangle size={20} color={theme.palette.warning.main} />
                  <Typography variant="h6" sx={{ color: theme.palette.warning.main }}>
                    Low Stock Items ({lowStockItems.length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  {lowStockItems.length > 0 ? (
                    lowStockItems.map((item, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Alert
                          severity="warning"
                          icon={<AlertTriangle size={20} />}
                          sx={{
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.warning.main, 0.1),
                            color: theme.palette.text.primary,
                            '& .MuiAlert-icon': { color: theme.palette.warning.main },
                          }}
                        >
                          <AlertTitle sx={{ fontWeight: 'bold' }}>{item.title}</AlertTitle>
                          Only {item.quantity} units remaining (Threshold: {item.threshold})
                        </Alert>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Alert
                        severity="success"
                        sx={{
                          borderRadius: 2,
                          backgroundColor: alpha(theme.palette.success.main, 0.1),
                        }}
                      >
                        <AlertTitle>All Good!</AlertTitle>
                        No low stock items to display.
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </Container>

      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </Box>
  );
};

export default Dashboard;